import React, { useState, useEffect } from "react";
import './resetPassword.scss';
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, InferType, ref } from "yup";
import { useForm } from "react-hook-form";
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { genOTPandSavePwd, selectOTPandPwdDetails } from './genOTPandSavePwdSlice';
import { OTPVerification } from './otpVerification';
import { LOGIN } from "../../../../utils/CONSTANTS";
import DotGlobe from "./DotGlobe";

const resetPwdSchema = object({
  password: string()
    .required("Please enter the Password")
    .matches(
      /(?=.*?[A-Z])/,
      "Please enter atleast one Uppercase"
    )
    .matches(
      /(?=.*?[a-z])/,
      "Please enter atleast one Lowercase"
    )
    .matches(
      /(?=.*?[0-9])/,
      "Please enter atleast one digit"
    )
    .matches(
      /(?=.*?[#?!@$%^&*-])/,
      "Please enter atleast one Special Character"
    )
    .matches(
      /.{8,}/,
      "Please enter minumum 8 characters"
    ),
  confirmPassword: string()
    .required("Please enter the Password")
    .oneOf([ref("password")], "Confirm Password does not match"),
});

type resetPwdProps = InferType<typeof resetPwdSchema>;

const calcStrength = (v: string) => {
  let s = 0;
  if (v.length >= 8) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/[0-9]/.test(v)) s++;
  if (/[#?!@$%^&*-]/.test(v)) s++;
  return s;
};

const BrandPanel = () => (
  <aside className="brandPanel" aria-hidden="true">
    <div className="brandGrid" />
    <DotGlobe className="brandGlobe" size={520} variant="bright" />
    <div className="brandPanelContent">
      <h2 className="brandTitle">TTEC Connect</h2>
      <p className="brandTagline">
        Everything you need to get started — your onboarding hub, in one place.
      </p>
    </div>
  </aside>
);

export function ResetPassword() {
  const params = useParams();
  const dispatch = useDispatch();
  const { status, state, requestType } = useSelector(selectOTPandPwdDetails);
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [strength, setStrength] = useState(0);
  const buffer = require('buffer/').Buffer;

  const { register: resetPwdRegister, handleSubmit: resetPwdHandle, formState: { errors: resetPwdErrors } } =
    useForm<resetPwdProps>({
      resolver: yupResolver(resetPwdSchema),
    });

  function handleSubmit() {
    const data = {
      "input": {
        "request": {
          "action": "ValidateNewUser",
          "personalEmailAddress": buffer.from(params.email, 'base64').toString('ascii'),
          "phoneNumber": "",
          "subAction": ""
          }
      },
      "requestType": {
        "type": 'genOtp'
      }
    }
    dispatch<any>(genOTPandSavePwd(data));
  }

  useEffect(() => {
    const data = {
      "input": {
        "employeeUid": buffer.from(params.id, 'base64').toString('ascii'),
        "password": "",
        "otpNumber": "",
        "action": "ResetPassword",
        "subAction": "validate",
        "linkUid": params.token
      },
      "requestType": {
        "type": 'validateLink'
      }
    };
    dispatch<any>(genOTPandSavePwd(data));
  }, [buffer, dispatch, params.id, params.token]);

  return (
    <div id="divReset">
      {requestType === 'validateLink' && status !== '200' && state !== 'pending' &&
        <div className="landingPage">
          <BrandPanel />
          <div className="loginContainer">
            <div className="center">
              <div className="warnMark">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h1 className="authH1">Link expired or invalid</h1>
              <p className="sub">
                For security reasons, password reset links expire after one hour.
                If you still need to reset your password, you can request a new
                reset link below.
              </p>
              <Link to={LOGIN} className="btn btn-primary">
                Request a new reset link
              </Link>
            </div>
          </div>
        </div>
      }
      {(status === '200' && (state === 'fulfilled' || state === 'pending') && requestType === 'validateLink') &&
        <div className="landingPage">
          <BrandPanel />
          <div className="loginContainer">
            <form className="Auth-form">
              <div className="eyebrow">Account recovery</div>
              <h1 className="authH1">Create new password</h1>
              <p className="sub">Choose a strong password you haven't used before.</p>

              <div className="field hasIcon">
                <input
                  id="rsPass"
                  type={showPass ? "text" : "password"}
                  placeholder=" "
                  {...resetPwdRegister("password")}
                  onChange={event => { setPassword(event.target.value); setStrength(calcStrength(event.target.value)); }}
                />
                <label htmlFor="rsPass">New password <span className="req">*</span></label>
                <button type="button" className="icon" aria-label="Show password" onClick={() => setShowPass(s => !s)}>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="2" />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              <div className="meter">
                {[1, 2, 3, 4].map((n) => (
                  <i key={n} className={n <= strength ? "on" + strength : ""} />
                ))}
              </div>
              <div className="meter-label">
                <span>8+ chars · upper · lower · number · symbol</span>
                <span>{["", "Weak", "Fair", "Good", "Strong"][strength]}</span>
              </div>
              {resetPwdErrors?.password && <p className="frmErrors mt-2">{resetPwdErrors?.password?.message}</p>}

              <div className="field">
                <input
                  id="rsPass2"
                  type="password"
                  placeholder=" "
                  {...resetPwdRegister("confirmPassword")}
                />
                <label htmlFor="rsPass2">Confirm password <span className="req">*</span></label>
              </div>
              <p className="frmErrors mt-2">{resetPwdErrors?.confirmPassword && resetPwdErrors?.confirmPassword?.message}</p>

              <button type="button" onClick={resetPwdHandle(handleSubmit)} className="btn btn-primary">
                Update password
              </button>
            </form>
          </div>
        </div>
      }
      {(((requestType === 'genOtp') && (state === 'fulfilled' || status === '200')) || (requestType === 'savePwd')) &&
        <OTPVerification details={{ pwd: password }} />
      }
    </div>
  );
}
